import React from 'react';
import '../../styles/Whot.css';
import { getShapeName, WHOT_DESIGN } from '../utils/whotDesign';

function ShapeSvg({ kind, title, className, style }) {
  const common = {
    viewBox: '0 0 100 100',
    width: '100%',
    height: '100%',
    role: title ? 'img' : 'presentation',
    'aria-label': title || undefined,
    className,
    style,
  };

  switch (kind) {
    case 'circle':
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <circle cx="50" cy="50" r="38" fill="currentColor" />
        </svg>
      );
    case 'square':
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <rect x="18" y="18" width="64" height="64" rx="6" fill="currentColor" />
        </svg>
      );
    case 'triangle':
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <polygon points="50,14 86,84 14,84" fill="currentColor" />
        </svg>
      );
    case 'cross':
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <rect x="42" y="14" width="16" height="72" rx="4" fill="currentColor" />
          <rect x="14" y="42" width="72" height="16" rx="4" fill="currentColor" />
        </svg>
      );
    case 'star':
    default:
      return (
        <svg {...common}>
          {title ? <title>{title}</title> : null}
          <path
            d="M50 10 L61 37 L90 37 L66 54 L75 82 L50 66 L25 82 L34 54 L10 37 L39 37 Z"
            fill="currentColor"
          />
        </svg>
      );
  }
}

export default function WhotCard({
  card,
  onClick,
  isSelected,
  isPlayable,
  isSmall = false,
  isFaceDown = false,
  badge,
  hideNumber = false,
  suppressPlayableStyles = false,
}) {
  const handleClick = () => {
    if (!onClick) return;

    if (isFaceDown) {
      onClick(card);
      return;
    }

    if (!card) return;
    if (isPlayable || card.number === 20) onClick(card);
  };

  if (isFaceDown) {
    const classes = [
      'whot-playing-card',
      'face-down',
      isSmall ? 'is-small' : '',
    ].filter(Boolean).join(' ');

    return (
      <div className={classes} onClick={handleClick}>
        <div className="card-back-pattern">
          <div className="card-back-logo">Whot</div>
        </div>
        {badge !== undefined && badge !== null && (
          <div className="whot-card-badge">{badge}</div>
        )}
      </div>
    );
  }

  if (!card) return null;

  const { number, shape } = card;
  const layout = WHOT_DESIGN.getCardLayout(number, shape);
  const shapeName = getShapeName(shape);

  const classes = [
    'whot-playing-card',
    'whot-playing-card--photo',
    isSelected ? 'selected' : '',
    isPlayable && !suppressPlayableStyles ? 'playable' : '',
    isSmall ? 'is-small' : '',
    layout.isWhot ? 'is-whot' : '',
    `shape-${shape}`,
  ].filter(Boolean).join(' ');

  const style = {
    opacity: suppressPlayableStyles ? 1 : (isPlayable ? 1 : 0.6),
    cursor: suppressPlayableStyles ? 'pointer' : (isPlayable ? 'pointer' : 'not-allowed'),
    '--whot-card-color': layout.color,
    '--whot-card-bg': WHOT_DESIGN.CARD_BG,
    '--whot-ink-red': layout.color,
  };

  return (
    <div
      className={classes}
      style={style}
      onClick={handleClick}
      data-number={layout.number}
      data-shape={shape}
    >
      {/* Corners (number + small symbol) */}
      <div className="photo-corner photo-corner--tl" aria-hidden="true">
        {!layout.isWhot ? (
          <>
            <div className="photo-corner-symbol">
              <ShapeSvg kind={layout.corners.kind} title={shapeName} style={{ color: 'var(--whot-ink-red)' }} />
            </div>
            {!hideNumber ? <div className="photo-corner-number">{layout.number}</div> : null}
          </>
        ) : (
          !hideNumber ? <div className="photo-corner-number">{layout.number}</div> : null
        )}
      </div>

      <div className="photo-corner photo-corner--br" aria-hidden="true">
        {!layout.isWhot ? (
          <>
            <div className="photo-corner-symbol">
              <ShapeSvg kind={layout.corners.kind} title={shapeName} style={{ color: 'var(--whot-ink-red)' }} />
            </div>
            {!hideNumber ? <div className="photo-corner-number">{layout.number}</div> : null}
          </>
        ) : (
          !hideNumber ? <div className="photo-corner-number">{layout.number}</div> : null
        )}
      </div>

      {/* Center */}
      <div className="photo-center" aria-hidden="true">
        {layout.isWhot ? (
          <div className="photo-whot">
            <div className="photo-whot-text">Whot</div>
          </div>
        ) : (
          <>
            <div className="photo-center-symbol">
              <ShapeSvg kind={layout.center.kind} style={{ color: 'var(--whot-ink-red)' }} />
            </div>
            {layout.centerNumber ? (
              <div className="photo-center-number">{layout.centerNumber}</div>
            ) : null}
          </>
        )}
      </div>

      {isSelected && <div className="selected-glow" style={{ color: layout.color }} />}
      {isPlayable && !isSelected && !suppressPlayableStyles && (
        <div className="playable-indicator" style={{ background: layout.color }} />
      )}
    </div>
  );
}

